export type StatusTextColorMode = 'auto' | 'dark' | 'light';
export interface StatusColorConfig {
  backgroundColor: string;
  textColorMode: StatusTextColorMode;
}

const DEFAULT_STATUS_BACKGROUND_COLOR = '#64748b';
const DEFAULT_STATUS_TEXT_COLOR_MODE: StatusTextColorMode = 'light';

export const normalizeHexColor = (value?: string | null): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(trimmed)) {
    return null;
  }

  if (trimmed.length === 4) {
    const r = trimmed.charAt(1);
    const g = trimmed.charAt(2);
    const b = trimmed.charAt(3);
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  return trimmed.toLowerCase();
};

export const hexToRgba = (value: string, alpha: number): string => {
  const normalized = normalizeHexColor(value);
  if (!normalized) {
    return `rgba(100, 116, 139, ${alpha})`;
  }

  const red = Number.parseInt(normalized.slice(1, 3), 16);
  const green = Number.parseInt(normalized.slice(3, 5), 16);
  const blue = Number.parseInt(normalized.slice(5, 7), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

export const getReadableTextColor = (_value: string): string => {
  return '#ffffff';
};

export const resolveStatusTextColor = (
  _backgroundColor: string,
  _textColorMode?: StatusTextColorMode | null,
): string => {
  return '#ffffff';
};

export const parseStatusColorConfig = (value?: string | null): StatusColorConfig => {
  if (!value) {
    return {
      backgroundColor: DEFAULT_STATUS_BACKGROUND_COLOR,
      textColorMode: DEFAULT_STATUS_TEXT_COLOR_MODE,
    };
  }

  const trimmed = value.trim();
  const normalizedHex = normalizeHexColor(trimmed);
  if (normalizedHex) {
    return {
      backgroundColor: normalizedHex,
      textColorMode: DEFAULT_STATUS_TEXT_COLOR_MODE,
    };
  }

  try {
    const parsed = JSON.parse(trimmed) as {
      backgroundColor?: string;
      bg?: string;
      textColorMode?: StatusTextColorMode;
      text?: StatusTextColorMode;
    };
    const backgroundColor =
      normalizeHexColor(parsed.backgroundColor ?? parsed.bg) ??
      DEFAULT_STATUS_BACKGROUND_COLOR;
    const textColorMode =
      parsed.textColorMode === 'dark' ||
      parsed.textColorMode === 'light' ||
      parsed.textColorMode === 'auto'
        ? parsed.textColorMode
        : parsed.text === 'dark' || parsed.text === 'light' || parsed.text === 'auto'
          ? parsed.text
          : DEFAULT_STATUS_TEXT_COLOR_MODE;

    return {
      backgroundColor,
      textColorMode,
    };
  } catch {
    return {
      backgroundColor: DEFAULT_STATUS_BACKGROUND_COLOR,
      textColorMode: DEFAULT_STATUS_TEXT_COLOR_MODE,
    };
  }
};

export const serializeStatusColorConfig = (
  backgroundColor?: string | null,
  textColorMode?: StatusTextColorMode | null,
): string => {
  const config = {
    backgroundColor:
      normalizeHexColor(backgroundColor) ?? DEFAULT_STATUS_BACKGROUND_COLOR,
    textColorMode:
      textColorMode === 'dark' || textColorMode === 'light' || textColorMode === 'auto'
        ? textColorMode
        : DEFAULT_STATUS_TEXT_COLOR_MODE,
  };

  return JSON.stringify(config);
};
